/**
 * Runtime toggle for Live GPT mode.
 *
 * Deterministic Demo ↔ Live GPT is controlled here so a single click
 * in the AppShell header flips every consumer at once. The initial
 * value comes from the AI_LIVE_ENABLED build flag; the toggle
 * overrides it at runtime and persists to localStorage so a page
 * refresh does not undo the demo-day setting.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AI_LIVE_ENABLED } from "@/constants/featureFlags";

const LiveModeContext = createContext(null);
const STORAGE_KEY = "nn-live-mode";

/**
 * Storage semantics:
 *   • absent       → follow AI_LIVE_ENABLED (default: live on)
 *   • "1"          → user explicitly enabled live (same as default)
 *   • "0"          → user explicitly opted OUT to DEMO mode
 * A stored "0" is the only way to keep DEMO as the runtime mode when
 * the server has a key available. This makes DEMO an explicit
 * opt-out, not the default.
 */
export function LiveModeProvider({ children }) {
  const [userPref, setUserPref] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === "0") return "off";       // explicit opt-out
      if (stored === "1") return "on";        // explicit opt-in
    } catch {}
    return AI_LIVE_ENABLED ? "on" : "off";     // fresh session default
  });

  const [health, setHealth] = useState({ checked: false, liveAvailable: false, model: "" });

  const refreshHealth = useCallback(async () => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/llm/health`;
      const res = await fetch(url);
      const j = await res.json();
      setHealth({ checked: true, liveAvailable: !!j.live_enabled, model: j.model || "" });
    } catch (e) {
      setHealth({ checked: true, liveAvailable: false, model: "" });
    }
  }, []);

  useEffect(() => {
    refreshHealth();
  }, [refreshHealth]);

  const setLive = useCallback((next) => {
    const pref = next ? "on" : "off";
    setUserPref(pref);
    try { localStorage.setItem(STORAGE_KEY, next ? "1" : "0"); } catch {}
  }, []);

  // Effective mode used by every consumer.
  // Live GPT engages automatically the moment the server reports a
  // key IS available, unless the user has explicitly toggled to DEMO.
  const liveActive = health.liveAvailable && userPref !== "off";

  const value = useMemo(() => ({
    live: userPref === "on",             // user-facing preference (does not force live if server has no key)
    liveAvailable: health.liveAvailable, // whether the server has any OpenAI-capable key
    liveActive,                          // the effective mode consumers use
    model: health.model,
    setLive,
    refreshHealth,
  }), [userPref, health.liveAvailable, health.model, liveActive, setLive, refreshHealth]);

  return <LiveModeContext.Provider value={value}>{children}</LiveModeContext.Provider>;
}

export function useLiveMode() {
  const ctx = useContext(LiveModeContext);
  if (!ctx) {
    return { live: false, liveAvailable: false, liveActive: false, model: "", setLive: () => {}, refreshHealth: () => {} };
  }
  return ctx;
}
